import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Marker, InfoWindow, useApiIsLoaded } from "@vis.gl/react-google-maps";
import { GoogleMap } from "../GoogleMaps/GoogleMap";
import { MapBoundsFitter } from "../GoogleMaps/components/MapBoundsFitter";
import { HeatmapLayer } from "../GoogleMaps/components/HeatmapLayer";
import { companyConfigurationsSelector } from "../../Reducers/companyConfigurtionsReducer";
import { useFetchCompanyInfosQuery } from "../../Apis/companyApi";
import { ChCheckIcon } from "../Icons/ch-analytics-icons";
import { STORE_MARKER_SVG } from "../Icons/store-marker-icon";
import { cx } from "../../Util/classHelpers";
import styles from "./chHeatmap.module.css";

const CompanyMarker = ({ companyId, latitude, longitude, name, markerIcon, infoVisible, onClick, onInfoClose }) => (
    <div key={companyId}>
        <Marker
            position={{ lat: latitude, lng: longitude }}
            title={name}
            icon={markerIcon}
            onClick={onClick}
        />
        {infoVisible && (
            <InfoWindow
                position={{ lat: latitude, lng: longitude }}
                onCloseClick={onInfoClose}
            >
                <div className="custom-popup-content">
                    <div>{name}</div>
                </div>
            </InfoWindow>
        )}
    </div>
);

const ChHeatmapContent = ({ data, loading }) => {
    const companyConfigurations = useSelector(companyConfigurationsSelector);
    const { data: partnerAccounts } = useFetchCompanyInfosQuery();
    const apiIsLoaded = useApiIsLoaded();
    const [selectedMarker, setSelectedMarker] = useState(null);

    const markerIcon = useMemo(() => {
        if (!apiIsLoaded || typeof window.google === "undefined" || !window.google.maps) return null;
        return {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(STORE_MARKER_SVG),
            scaledSize: new window.google.maps.Size(40, 45),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(20, 45),
        };
    }, [apiIsLoaded]);

    const deliveryCount = useMemo(() =>
        data.flatMap((item) => item.getLocations().map((l) => l.getWeight()))
            .reduce((a, b) => a + b, 0),
        [data]);

    const maxIntensity = useMemo(() =>
        data.map((item) => item.getMaxIntensity()).reduce((a, b) => Math.max(a, b), 0.1),
        [data]);

    const locations = useMemo(() =>
        data.flatMap((item) => item.getLocations()),
        [data]);

    const pickupLocations = useMemo(() =>
        data
            .map((item) => {
                const pickup = companyConfigurations?.getPickupLocation(item.getCompanyId());
                if (!pickup) return null;
                const companyId = item.getCompanyId();
                const name = partnerAccounts?.getAccountDetailsByCompanyId(companyId)?.companyName ?? "";
                return { companyId, latitude: pickup.latitude, longitude: pickup.longitude, name };
            })
            .filter(Boolean),
        [companyConfigurations, partnerAccounts, data]);

    const allLocations = useMemo(() => [
        ...locations.map((l) => ({ latitude: l.getLatitude(), longitude: l.getLongitude() })),
        ...pickupLocations.map((p) => ({ latitude: p.latitude, longitude: p.longitude })),
    ], [locations, pickupLocations]);

    const heatmapData = useMemo(() => {
        if (!apiIsLoaded || typeof window.google === "undefined" || !window.google.maps) return [];
        return locations.map((l) => ({
            location: new window.google.maps.LatLng(l.getLatitude(), l.getLongitude()),
            weight: l.getWeight(),
        }));
    }, [locations, apiIsLoaded]);

    return (
        <>
            <div className={styles.summaryCard}>
                <div className={styles.cardTop}>
                    <span className={styles.cardTitle}>Total Deliveries</span>
                    <span className={styles.iconBg}><ChCheckIcon /></span>
                </div>
                <div className={cx(styles.value, loading && styles.skel)}>
                    {loading ? "" : deliveryCount.toLocaleString()}
                </div>
                <div className={styles.sub}>delivered this period</div>
            </div>

            <div className={styles.mapWrap}>
                <GoogleMap center={{ lat: 0, lng: 0 }} zoom={2}>
                    <MapBoundsFitter locations={allLocations} />
                    {heatmapData.length > 0 && (
                        <HeatmapLayer data={heatmapData} maxIntensity={maxIntensity} radius={30} />
                    )}
                    {markerIcon && pickupLocations.map((p) => (
                        <CompanyMarker
                            key={p.companyId}
                            {...p}
                            markerIcon={markerIcon}
                            infoVisible={selectedMarker === p.companyId}
                            onClick={() => setSelectedMarker(p.companyId)}
                            onInfoClose={() => setSelectedMarker(null)}
                        />
                    ))}
                </GoogleMap>
            </div>
        </>
    );
};

export default ChHeatmapContent;
